import React, { useState, useEffect, useCallback } from 'react';
import { boardAPI } from '../../../api';
const XLSX = require('xlsx');

type ContentType = 'comment' | 'board' | 'suggestion';
type BoardType = '' | 'free' | 'qna' | 'community';

const BOARD_TYPE_LABELS: Record<string, string> = {
  free: '자유게시판',
  qna: 'Q&A',
  community: '커뮤니티',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '미확인',
  checked: '확인',
  done: '처리완료',
};

const ContentMonitor: React.FC = () => {
  const [contentType, setContentType] = useState<ContentType>('comment');
  const [boardType, setBoardType] = useState<BoardType>('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const res = await boardAPI.monitor(contentType, contentType === 'board' ? boardType : undefined, contentType === 'board' ? category : undefined);
      setItems(res.data);
    } catch (e) {
      alert('데이터 로드 실패');
    }
    setLoading(false);
  }, [contentType, boardType, category]);

  // 게시판 타입 변경 시 카테고리 목록 로드
  useEffect(() => {
    if (contentType === 'board' && boardType) {
      boardAPI.categories(boardType).then(res => {
        setCategories(res.data);
        setCategory('');
      }).catch(() => setCategories([]));
    } else {
      setCategories([]);
      setCategory('');
    }
  }, [contentType, boardType]);

  useEffect(() => { fetchItems(); }, [contentType, boardType]);

  // 단일 삭제
  const handleDelete = async (id: number) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    try {
      if (contentType === 'comment') await boardAPI.deleteComment(id);
      else if (contentType === 'board') await boardAPI.deletePost(id);
      else if (contentType === 'suggestion') await boardAPI.deleteSuggestion(id);
      setItems(prev => prev.filter(item => item.id !== id));
    } catch (e) {
      alert('삭제 실패');
    }
  };

  // 선택 삭제
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}개를 삭제하시겠습니까?`)) return;
    for (const id of selectedIds) {
      try {
        if (contentType === 'comment') await boardAPI.deleteComment(id);
        else if (contentType === 'board') await boardAPI.deletePost(id);
        else if (contentType === 'suggestion') await boardAPI.deleteSuggestion(id);
      } catch (e) {}
    }
    setItems(prev => prev.filter(item => !selectedIds.has(item.id)));
    setSelectedIds(new Set());
  };

  // 체크박스 토글
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(i => i.id)));
    }
  };

  // 검색 필터
  const filtered = items.filter(item => {
    if (!searchKeyword.trim()) return true;
    const kw = searchKeyword.toLowerCase();
    return (
      item.content?.toLowerCase().includes(kw) ||
      item.title?.toLowerCase().includes(kw) ||
      item.author?.toLowerCase().includes(kw)
    );
  });

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (filtered.length === 0) { alert('다운로드할 데이터가 없습니다.'); return; }

    let rows: any[] = [];
    if (contentType === 'comment') {
      rows = filtered.map(c => ({
        ID: c.id,
        작성자: c.author,
        내용: c.content,
        문제ID: c.question_id,
        작성일: c.created_at,
      }));
    } else if (contentType === 'board') {
      rows = filtered.map(b => ({
        ID: b.id,
        게시판: BOARD_TYPE_LABELS[b.board_type] || b.board_type,
        카테고리: b.category,
        작성자: b.author,
        제목: b.title,
        내용: b.content,
        작성일: b.created_at,
      }));
    } else if (contentType === 'suggestion') {
      rows = filtered.map(s => ({
        ID: s.id,
        작성자: s.author,
        제목: s.title,
        내용: s.content,
        상태: STATUS_LABELS[s.status] || s.status,
        작성일: s.created_at,
      }));
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, '외부글');
    const typeLabel = contentType === 'comment' ? '함께공부' : contentType === 'board' ? '게시판' : '건의사항';
    XLSX.writeFile(wb, `외부글_${typeLabel}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title"><span className="icon">🗂️</span>외부글 관리</div>
      </div>

      {/* 탭 + 툴바 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>

            {/* 콘텐츠 타입 탭 */}
            {(['comment', 'board', 'suggestion'] as ContentType[]).map(t => (
              <button key={t}
                className={`btn btn-sm ${contentType === t ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setContentType(t); setBoardType(''); setSearchKeyword(''); }}>
                {t === 'comment' ? '💬 함께공부' : t === 'board' ? '📋 게시판' : '📝 건의사항'}
                {contentType === t && <span style={{ marginLeft: 4, fontWeight: 700 }}>({items.length})</span>}
              </button>
            ))}

            {/* 게시판 타입 필터 */}
            {contentType === 'board' && (
              <select className="form-select" style={{ height: 28, fontSize: 12, width: 110 }}
                value={boardType} onChange={e => { setBoardType(e.target.value as BoardType); setCategory(''); }}>
                <option value="">전체</option>
                <option value="free">자유게시판</option>
                <option value="qna">Q&A</option>
                <option value="community">커뮤니티</option>
              </select>
            )}

            {/* 카테고리 필터 (커뮤니티 선택 시) */}
            {contentType === 'board' && boardType === 'community' && categories.length > 0 && (
              <select className="form-select" style={{ height: 28, fontSize: 12, width: 110 }}
                value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">카테고리 전체</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}

            {/* 검색 */}
            <input className="form-control" placeholder="작성자 / 내용 검색..."
              value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)}
              style={{ height: 28, fontSize: 12, width: 180 }} />

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {/* 선택 삭제 */}
              {selectedIds.size > 0 && (
                <button className="btn btn-sm" onClick={handleBulkDelete}
                  style={{ background: '#e74c3c', color: 'white', fontSize: 12 }}>
                  🗑️ 선택 삭제 ({selectedIds.size})
                </button>
              )}
              {/* 새로고침 */}
              <button className="btn btn-ghost btn-sm" onClick={fetchItems} style={{ fontSize: 12 }}>
                🔄 새로고침
              </button>
              {/* 엑셀 다운로드 */}
              <button className="btn btn-primary btn-sm" onClick={handleExcelDownload} style={{ fontSize: 12 }}>
                📥 엑셀 다운로드
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 목록 테이블 */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>로딩 중...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>데이터가 없습니다.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '8px 10px', width: 36 }}>
                    <input type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll} />
                  </th>
                  <th style={{ padding: '8px 10px', width: 50, textAlign: 'center' }}>ID</th>
                  {contentType === 'board' && (
                    <th style={{ padding: '8px 10px', width: 80, textAlign: 'center' }}>게시판</th>
                  )}
                  <th style={{ padding: '8px 10px', width: 90, textAlign: 'center' }}>작성자</th>
                  {contentType !== 'comment' && (
                    <th style={{ padding: '8px 10px', width: 160 }}>제목</th>
                  )}
                  <th style={{ padding: '8px 10px' }}>내용</th>
                  {contentType === 'suggestion' && (
                    <th style={{ padding: '8px 10px', width: 70, textAlign: 'center' }}>상태</th>
                  )}
                  <th style={{ padding: '8px 10px', width: 130, textAlign: 'center' }}>작성일</th>
                  <th style={{ padding: '8px 10px', width: 60, textAlign: 'center' }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} style={{
                    borderBottom: '1px solid var(--border)',
                    background: selectedIds.has(item.id) ? '#fef3c7' : 'white',
                  }}>
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      <input type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)} />
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {item.id}
                    </td>
                    {contentType === 'board' && (
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                        <span style={{ fontSize: 11, background: '#e8f4fd', color: '#2980b9', padding: '2px 6px', borderRadius: 4 }}>
                          {BOARD_TYPE_LABELS[item.board_type] || item.board_type}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {item.author}
                    </td>
                    {contentType !== 'comment' && (
                      <td style={{ padding: '7px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </td>
                    )}
                    <td style={{ padding: '7px 10px', maxWidth: 300 }}>
                      <div style={{ maxHeight: 60, overflow: 'hidden', lineHeight: 1.5, color: 'var(--text-primary)' }}>
                        {item.content}
                      </div>
                    </td>
                    {contentType === 'suggestion' && (
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 11, padding: '2px 6px', borderRadius: 4,
                          background: item.status === 'done' ? '#d5f5e3' : item.status === 'checked' ? '#fef9c3' : '#fdecea',
                          color: item.status === 'done' ? '#1e8449' : item.status === 'checked' ? '#b7950b' : '#c0392b',
                        }}>
                          {STATUS_LABELS[item.status]}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '7px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      {item.created_at}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      <button onClick={() => handleDelete(item.id)}
                        style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 하단 요약 */}
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
        총 {filtered.length}건
        {selectedIds.size > 0 && ` / 선택 ${selectedIds.size}건`}
      </div>
    </div>
  );
};

export default ContentMonitor;
